import { IonCard, IonCardContent, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import React from 'react';
import { useIntl } from 'react-intl';
import { DqText } from '../dq-text';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import { Firmware } from 'src/models/firmware';
import { pencil } from 'ionicons/icons';
import { GenericMessages } from '../../intl/GenericMessages';
import { FirmwareTier } from '../../models/FirmwareTier';
import { Organization } from '../../models/organization';
import { getOrgName } from '../../utils/helpers/referenceHelpers';

interface FirmwareDetailsCardProps {
  /** The details of the selected firmware. */
  firmware: Firmware | undefined;
  /** The callback triggered when the edit button is clicked. */
  onEditFirmwareDetails: () => void;
  /** The firmware tiers available */
  firmwareTiers: FirmwareTier[];
  organizations: Organization[];
}

export const FirmwareDetailsCard: React.FC<FirmwareDetailsCardProps> = ({
  firmware,
  firmwareTiers,
  onEditFirmwareDetails,
  organizations,
}) => {
  const { formatMessage } = useIntl();
  const displayReleaseType = firmware?.releaseType
    ? `${firmware.releaseType.charAt(0).toUpperCase()}${firmware.releaseType.slice(1)}`
    : formatMessage(GenericMessages.unknownPlaceholder);
  const firmwareTierName = firmwareTiers?.find(f => f._id === firmware?.firmwareTierId)?.tierName;

  const formattedUsers = React.useMemo(() => {
    if (!firmware?.releaseType) {
      return null;
    }

    if (firmware.releaseType === 'beta') {
      return firmware.beta ? firmware.beta.join('\n') : null;
    }
    if (firmware.releaseType === 'test') {
      return firmware.users ? firmware.users.join('\n') : null;
    }

    return null;
  }, [firmware?.beta, firmware?.releaseType, firmware?.users]);

  const formattedOrganizations =
    firmware?.releaseType === 'test'
      ? firmware.orgs?.map(orgId => getOrgName(orgId, organizations)).join('\n')
      : firmware?.releaseType === 'custom'
        ? getOrgName(firmware?.custom, organizations)
        : null;

  return (
    <IonCard className="firmware-card">
      <IonCardContent className="firmware-card-content">
        <IonGrid>
          <IonRow>
            <IonCol size="3">
              <DqText variant="body-small">{formatMessage(FirmwareMessages.displayName)}</DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-small">{formatMessage(FirmwareMessages.version)}</DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-small">{formatMessage(FirmwareMessages.id)}</DqText>
            </IonCol>
            <IonCol className="ion-justify-content-between" size="3">
              <DqText variant="body-small">
                {formatMessage(FirmwareMessages.type)}
                {firmware?.releaseType === 'paid' && !!firmwareTierName
                  ? ` - ${formatMessage(FirmwareMessages.tier)}`
                  : ''}
              </DqText>
              {/** Custom style on icon to get it inline with headers. */}
              <IonIcon
                icon={pencil}
                color="dark"
                style={{ fontSize: '20px', padding: 0, margin: 0 }}
                onClick={() => onEditFirmwareDetails()}
              />
            </IonCol>
          </IonRow>
          <IonRow className="firmware-value-row">
            <IonCol size="3">
              <DqText variant="body-strong">{firmware?.displayName}</DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-strong">{firmware?.version}</DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-strong">{firmware?._id}</DqText>
            </IonCol>
            <IonCol className="ion-justify-content-between" size="3">
              <DqText variant="body-strong">
                {displayReleaseType}
                {firmware?.releaseType === 'paid' && !!firmwareTierName ? ` - ${firmwareTierName}` : ''}
              </DqText>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="3">
              <DqText variant="body-small">{formatMessage(FirmwareMessages.status)}</DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-small">{formatMessage(FirmwareMessages.file)}</DqText>
            </IonCol>
          </IonRow>
          <IonRow className="firmware-value-row">
            <IonCol size="3">
              <DqText variant="body-strong">
                {firmware?.isHidden === 'true'
                  ? formatMessage(FirmwareMessages.archived)
                  : formatMessage(FirmwareMessages.active)}
              </DqText>
            </IonCol>
            <IonCol size="3">
              <DqText variant="body-strong">{firmware?.fileName}</DqText>
            </IonCol>
          </IonRow>

          {(formattedUsers || formattedOrganizations) && (
            <IonRow>
              {formattedUsers && (
                <IonCol size="6">
                  <DqText variant="body-small">{formatMessage(FirmwareMessages.firmwareUsersLabel)}</DqText>
                </IonCol>
              )}
              {formattedOrganizations && (
                <IonCol size="6">
                  <DqText variant="body-small">{formatMessage(FirmwareMessages.firmwareOrganizationsLabel)}</DqText>
                </IonCol>
              )}
            </IonRow>
          )}
          {(formattedUsers || formattedOrganizations) && (
            <IonRow>
              {formattedUsers && (
                <IonCol size="6">
                  <DqText variant="body-strong" style={{ 'white-space': 'pre-wrap' }}>
                    {formattedUsers}
                  </DqText>
                </IonCol>
              )}
              {formattedOrganizations && (
                <IonCol size="6">
                  <DqText variant="body-strong" style={{ 'white-space': 'pre-wrap' }}>
                    {formattedOrganizations}
                  </DqText>
                </IonCol>
              )}
            </IonRow>
          )}
        </IonGrid>
      </IonCardContent>
    </IonCard>
  );
};
